.list {
  display: grid;
  gap: var(--space-4);
  list-style: none;
}

.deal {
  display: grid;
  gap: var(--space-4);
}

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

.names {
  display: grid;
  gap: var(--space-1);
}

.product {
  font-size: var(--text-xl);
}

.host {
  font-family: var(--font-figure);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* The sentence that says what the review state means. It sits directly under the pill
 * because the word alone ("Disabled") does not tell a founder what changed for them. */
.meaning {
  color: var(--text);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
  padding-inline-start: var(--space-3);
  border-inline-start: 2px solid var(--brass-rule);
}

.description {
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

.value {
  display: grid;
  gap: var(--space-2);
}

.priceRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-3);
}

.now {
  font-size: var(--text-xl);
  font-weight: 500;
}

.was {
  color: var(--text-muted);
}

.off {
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--accent-strong);
  background: var(--accent-soft);
  border-radius: var(--radius-sm);
  padding-block: 2px;
  padding-inline: var(--space-2);
}

.notes {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  list-style: none;
}

.facts {
  display: grid;
  gap: var(--space-3);
  margin: 0;
  padding-block-start: var(--space-4);
  border-block-start: 1px solid var(--border);
}

.fact {
  display: grid;
  gap: var(--space-1);
}

.fact dt {
  font-family: var(--font-figure);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.fact dd {
  margin: 0;
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.link,
.unavailable {
  font-size: var(--text-sm);
}

.unavailable {
  color: var(--text-muted);
}

/*
 * The panel is a list plus the way a founder adds to it. Canon decision `D-008` moved
 * submission behind the session, so creating a deal and watching it move through review are
 * one surface rather than two (canon APP-009).
 */
.panel {
  display: grid;
  gap: var(--space-5);
}

/*
 * A founder with no deals meets the form itself rather than a panel explaining that there is
 * nothing here. It is the only thing on the panel, so it carries no separator: there is
 * nothing above it to be separated from.
 */
.first {
  display: grid;
}

/*
 * The same form, appended below an existing list. Here the rule matters — it is what keeps
 * a new submission from reading as an edit of the last card above it.
 */
.another {
  display: grid;
  border-block-start: 3px solid var(--brass-rule);
}

.add {
  display: flex;
}

@media (min-width: 40rem) {
  .facts {
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
    gap: var(--space-4);
  }
}

/*
 * The edit control sits at the foot of the card, after the facts it changes. A founder
 * reads what the deal says, then decides to change it — a button above the content would
 * be asking them to act before they had looked.
 */
.edit {
  display: flex;
}

/*
 * The confirmation of a saved edit. It uses the brass rule the review-state sentence uses,
 * because it is the same kind of statement: what just happened to this deal's standing.
 * It is not green — an edit that sends a deal back to review is not a success to celebrate,
 * and one visual treatment for both outcomes keeps the words doing the work.
 */
.saved {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border-inline-start: 3px solid var(--brass-rule);
  border-radius: var(--radius-sm);
  background: var(--brass-soft);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.savedTitle {
  font-weight: 600;
  font-size: var(--text-base);
}

/*
 * The empty state (`APP-016`), drawn by `contracts/design/app-desk/` artboard 1.
 *
 * A founder with no deals used to be handed the submission form itself. The form is eight
 * fields and a terms acceptance; the canvas puts one sentence and one button in front of it,
 * because this is the first screen every founder sees and the first thing it should do is say
 * what submitting is for.
 */
.empty {
  display: grid;
  gap: var(--space-3);
  justify-items: start;
}

.emptyTitle {
  font-size: var(--text-lg);
}

.emptyBody {
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

/*
 * The operator's outstanding note (APP-020, decision D-016).
 *
 * Built from the same parts as `.saved` — this is Dealbid speaking to the founder — but ruled
 * in `--warning` rather than brass, because it is a request that is still open rather than a
 * confirmation of something done. The colour is never the message: every one of these carries
 * a heading naming what it is, so the block reads correctly with no colour perceived at all.
 *
 * It sits directly under `.meaning` and above the deal's own content. A founder whose deal is
 * in this state came to find out what to do about it.
 */
.request {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border-inline-start: 3px solid var(--warning);
  border-radius: var(--radius-sm);
  background: var(--brass-soft);
  max-inline-size: var(--measure);
}

.requestTitle {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text);
}

.requestNote {
  font-size: var(--text-sm);
  color: var(--text);
  /* Operator prose: it can run to a paragraph, carry line breaks, and contain a long URL. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/*
 * The decision history (APP-020).
 *
 * A native `<details>`, so the disclosure is keyboard-operable and announced correctly with no
 * script of ours in the path. Closed by default: it is deliberately secondary to `.request`.
 */
.history {
  border-block-start: 1px solid var(--border);
  padding-block-start: var(--space-3);
}

.historySummary {
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--text-muted);
  /* A comfortable touch target without moving the row it sits in. */
  padding-block: var(--space-2);
}

.historySummary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.historyList {
  list-style: none;
  display: grid;
  gap: var(--space-3);
  margin-block-start: var(--space-2);
}

.historyItem {
  display: grid;
  gap: var(--space-1);
  padding-inline-start: var(--space-3);
  border-inline-start: 2px solid var(--border);
}

.historyHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2) var(--space-3);
}

.historyAction {
  font-size: var(--text-sm);
  font-weight: 600;
}

.historyWhen {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.historyNote {
  font-size: var(--text-sm);
  color: var(--text-muted);
  max-inline-size: var(--measure);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
